import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound, Loader2, ShieldCheck, UserPlus } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  changePassword,
  createUser,
  getUsers,
  updateUserPermissions,
  type AppUser,
  type UserPermissionsUpdate,
} from '@/lib/api/auth/users'
import { apiErrorMessage } from '@/lib/api-client'
import { useAuth } from '@/components/providers/auth-provider'
import { formatDate } from '@/lib/utils'
import { CollectionsSection } from '@/pages/settings/_components/collections-section'

export function SettingsPage() {
  const { t } = useTranslation()
  const { user } = useAuth()

  return (
    <div className="scrollbar-thin flex-1 overflow-y-auto p-6">
      <div className="mx-auto max-w-3xl space-y-6">
        <div>
          <h2 className="text-base font-semibold">{t('settings.title')}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {user?.name} · {user?.username}
          </p>
        </div>

        <PasswordSection />
        {user?.is_admin && <UsersSection />}
        {user?.is_admin && <CollectionsSection />}
      </div>
    </div>
  )
}

function PasswordSection() {
  const { t } = useTranslation()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [repeat, setRepeat] = useState('')

  const mutation = useMutation({
    mutationFn: () => changePassword(current, next),
    onSuccess: () => {
      toast.success(t('settings.password.changed'))
      setCurrent('')
      setNext('')
      setRepeat('')
    },
    onError: (e) =>
      toast.error(t('settings.password.changeFailed'), { description: apiErrorMessage(e) }),
  })

  const mismatch = !!repeat && next !== repeat
  const tooShort = !!next && next.length < 8

  return (
    <section className="rounded-md border border-border p-4">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-medium">
        <KeyRound className="size-4 text-muted-foreground" />
        {t('settings.password.section')}
      </h3>
      <form
        className="space-y-2"
        onSubmit={(e) => {
          e.preventDefault()
          if (!mismatch && !tooShort) mutation.mutate()
        }}
      >
        <Input
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          placeholder={t('settings.password.current')}
          autoComplete="current-password"
        />
        <Input
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          placeholder={t('settings.password.new')}
          autoComplete="new-password"
        />
        <Input
          type="password"
          value={repeat}
          onChange={(e) => setRepeat(e.target.value)}
          placeholder={t('settings.password.repeat')}
          autoComplete="new-password"
        />
        {(mismatch || tooShort) && (
          <p className="text-xs text-danger">
            {mismatch ? t('settings.password.mismatch') : t('settings.password.tooShort')}
          </p>
        )}
        <Button type="submit" disabled={!next || mismatch || tooShort || mutation.isPending}>
          {mutation.isPending && <Loader2 className="animate-spin" />}
          {t('settings.password.submit')}
        </Button>
      </form>
    </section>
  )
}

function UsersSection() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { user } = useAuth()
  const users = useQuery({ queryKey: ['users'], queryFn: getUsers })
  const [form, setForm] = useState({ username: '', name: '', email: '', password: '', is_admin: false })

  const create = useMutation({
    mutationFn: () => createUser(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      toast.success(t('settings.users.created', { username: form.username }), {
        description: t('settings.users.createdHint'),
      })
      setForm({ username: '', name: '', email: '', password: '', is_admin: false })
    },
    onError: (e) =>
      toast.error(t('settings.users.createFailed'), { description: apiErrorMessage(e) }),
  })

  return (
    <section className="rounded-md border border-border p-4">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-medium">
        <ShieldCheck className="size-4 text-muted-foreground" />
        {t('settings.users.section')}
        <Badge variant="primary" size="sm">
          {t('auth.admin')}
        </Badge>
      </h3>

      <p className="mb-3 text-xs text-muted-foreground">
        {t('settings.users.permissionsHint')}
      </p>

      <div className="mb-4 divide-y divide-border/60 rounded-md border border-border">
        {users.data?.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2">
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium">{u.name}</p>
              <p className="truncate text-[11px] text-muted-foreground">
                {u.username}{u.email ? ` · ${u.email}` : ''}
              </p>
            </div>
            {u.is_admin && <Badge variant="primary">{t('auth.admin')}</Badge>}
            {u.has_password === false && (
              <Badge variant="warning">{t('settings.users.noPassword')}</Badge>
            )}
            {u.is_active === false && <Badge variant="danger">{t('settings.users.closed')}</Badge>}
            {u.id === user?.id ? (
              <span className="shrink-0 text-[11px] text-muted-foreground">
                {t('settings.users.currentUser')}
              </span>
            ) : (
              <PermissionControls target={u} />
            )}
            <span className="w-28 shrink-0 text-right text-[10px] text-muted-foreground">
              {u.last_login
                ? t('settings.users.lastLogin', { date: formatDate(u.last_login) })
                : t('settings.users.neverLoggedIn')}
            </span>
          </div>
        ))}
        {!users.data?.length && (
          <p className="p-3 text-xs text-muted-foreground">{t('settings.users.empty')}</p>
        )}
      </div>

      <form
        className="space-y-2"
        onSubmit={(e) => {
          e.preventDefault()
          create.mutate()
        }}
      >
        <p className="text-xs font-medium text-muted-foreground">{t('settings.users.newUser')}</p>
        <div className="grid grid-cols-2 gap-2">
          <Input
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            placeholder={t('settings.users.usernamePlaceholder')}
            required
          />
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={t('settings.users.namePlaceholder')}
          />
        </div>
        <Input
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder={t('settings.users.emailPlaceholder')}
        />
        <Input
          type="password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          placeholder={t('settings.users.tempPasswordPlaceholder')}
          autoComplete="new-password"
          required
        />
        <label className="flex cursor-pointer items-center gap-2 text-xs">
          <Checkbox
            checked={form.is_admin}
            onCheckedChange={(v) => setForm({ ...form, is_admin: v === true })}
          />
          {t('settings.users.isAdmin')}
        </label>
        <Button
          type="submit"
          disabled={!form.username || form.password.length < 8 || create.isPending}
        >
          {create.isPending ? <Loader2 className="animate-spin" /> : <UserPlus />}
          {t('settings.users.create')}
        </Button>
      </form>
    </section>
  )
}

function PermissionControls({ target }: { target: AppUser }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const update = useMutation({
    mutationFn: (permissions: UserPermissionsUpdate) =>
      updateUserPermissions(target.id, permissions),
    onSuccess: (updated) => {
      qc.setQueryData<AppUser[]>(['users'], (current) =>
        current?.map((u) => (u.id === updated.id ? updated : u)),
      )
      toast.success(t('settings.users.permissionsUpdated', { username: updated.username }))
    },
    onError: (e) =>
      toast.error(t('settings.users.permissionsUpdateFailed'), {
        description: apiErrorMessage(e),
      }),
  })

  return (
    <div className="flex shrink-0 items-center gap-3">
      <label className="flex cursor-pointer items-center gap-1.5 text-[11px]">
        <Checkbox
          checked={target.is_admin === true}
          disabled={update.isPending}
          onCheckedChange={(checked) => update.mutate({ is_admin: checked === true })}
        />
        {t('settings.users.administrator')}
      </label>
      <label className="flex cursor-pointer items-center gap-1.5 text-[11px]">
        <Checkbox
          checked={target.is_active !== false}
          disabled={update.isPending}
          onCheckedChange={(checked) => update.mutate({ is_active: checked === true })}
        />
        {t('settings.users.active')}
      </label>
      {update.isPending && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
    </div>
  )
}
